import { useCallback, useMemo } from 'react';

import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';

import styles from './ChangePlanStatusModal.module.scss';

interface IChangePlanStatusConfirmModalProps {
  visible: boolean;
  planStatus: ErknmPlanStatusesEnum | undefined;
  onApply: () => void;
  onClose: () => void;
}

export const ChangePlanStatusConfirmModal = ({
  visible,
  planStatus,
  onApply,
  onClose,
}: IChangePlanStatusConfirmModalProps) => {
  const handleSaveModalButton = useCallback(() => {
    onApply();
    onClose();
  }, [onApply, onClose]);

  const confirmText = useMemo(() => {
    if (planStatus === ErknmPlanStatusesEnum.UNDER_CONSIDERATION) {
      return (
        <>
          <h2 className="shared-title__with-margin shared-title__bold">Уважаемый пользователь!</h2>
          <div>Данное действие является необратимым!</div>
          <div>Перевод плана в статус &quot;На рассмотрении&quot; сделает невозможным:</div>
          <ul className={styles.ModalList}>
            <li>Удаление плана и КНМ в его составе;</li>
            <li>Редактирование обязательных для направления на согласование полей КНМ;</li>
            <li>Добавление новых КНМ в план.</li>
          </ul>
          <div>
            Вы хотите завершить формирование плана и продолжить перевод плана в статус &quot;На рассмотрении&quot;?
          </div>
        </>
      );
    }
    if (planStatus === ErknmPlanStatusesEnum.CONSIDERED) {
      return (
        <>
          <h2 className="shared-title__with-margin shared-title__bold">Уважаемый пользователь!</h2>
          <div>Данное действие является необратимым!</div>
          <div>Перевод плана в статус &quot;Рассмотрен&quot; сделает невозможным:</div>
          <ul className={styles.ModalList}>
            <li>Изменение статусов согласования КНМ в составе плана;</li>
            <li>Добавление комментариев к КНМ в составе плана.</li>
          </ul>
          <div>
            Нажимая на кнопку &quot;Да&quot; в этой форме, вы подтверждаете, что завершили работу с данным планом и не
            планируете вносить в него изменения.
          </div>
          <div>
            Вы хотите завершить работу над рассмотрением плана и продолжить перевод плана в статус
            &quot;Рассмотрен&quot;?
          </div>
        </>
      );
    }
    if (planStatus === ErknmPlanStatusesEnum.APPROVED) {
      return (
        <>
          <h2 className="shared-title__with-margin shared-title__bold">Уважаемый пользователь!</h2>
          <div>Данное действие является необратимым!</div>
          <div>Перевод плана в статус &quot;Утвержден&quot; сделает невозможным:</div>
          <ul className={styles.ModalList}>
            <li>Обжалование решения прокурора об исключении КНМ из плана;</li>
            <li>Внесение изменений в КНМ в соответствии с замечаниями прокурора.</li>
          </ul>
          <div>Информируем, что после утверждения план КНМ будет опубликован в открытой части ФГИС ЕРКНМ.</div>
          <div>
            Все КНМ по которым не получены результаты рассмотрения жалоб на их исключение прокурором, будут переведены в
            статус &quot;Исключено&quot;.
          </div>
          <div>Вы хотите завершить работу над планом и продолжить перевод плана в статус &quot;Утвержден&quot;?</div>
        </>
      );
    }
  }, [planStatus]);

  return (
    <Modal modalBodyClassName={styles.ModalBody} visible={visible} onClose={onClose}>
      <ModalHeader sizeText="small" title="Подтверждение смены статуса плана" color="black" />
      <ModalBody border="bottom" size="content">
        {confirmText}
      </ModalBody>
      <ModalActions placement="space-between">
        <Button text="Нет" size="medium" onClick={onClose} />
        <Button text="Да" size="medium" onClick={handleSaveModalButton} />
      </ModalActions>
    </Modal>
  );
};
